import React from 'react';
import { Progress } from '../ui';
import { formatMoney, formatPercent } from '../../lib/format';

/**
 * Single-row KPI strip for the project-detail chrome. Replaces the
 * legacy 8-card grid (Contract / SOV / Billed / Remaining / Variance /
 * Days / Collected / Outstanding).
 *
 * Per Schedule handoff §3 "KPI Strip":
 *   - 56px min height, 10px/24px padding
 *   - Each cell: small uppercase label + mono value
 *   - **Empty-state muting**: when a value is 0 / $0 / 0%, BOTH the label
 *     and the value drop to --text-4 so populated metrics breathe.
 *   - 18px gap between cells with a 1px × 28px --border-subtle divider
 *   - Inline Billed Progress on the right (180px wide)
 *   - Retainage Held surfaces inline next to the Billed to date cell
 *     (orange `--warning` tone) when there's any retainage withheld.
 *
 * Pass any value as null/undefined/0 to trigger the muted empty state.
 *
 * Note: the original spec had an expand chevron + 4-tile drawer (Burn
 * rate / Retainage held / This month billed / Forecast at completion).
 * Removed 2026-05-28 — all four values were $0 on every project and
 * the affordance was carrying state for no observable benefit.
 */
export default function KpiStrip({
  // Primary cells
  contractValue,
  billedToDate,
  remaining,
  daysRemaining,
  collectedToDate,
  outstanding,
  // Used only for the inline retainage annotation next to Billed to date
  retainageHeld,
  // Inline billed progress
  billedPercent,
  // Optional handler for Collected (click → edit, parent decides UX)
  onEditCollected,
}) {
  // Inline retainage annotation next to Billed to date. Only render
  // when there's actually retainage withheld -- a $0 retainage line
  // would just be noise. Orange text reuses the warning token so it
  // pops against the neutral KPI value without screaming.
  const retainageInline = (Number(retainageHeld) || 0) > 0
    ? `+ ${formatMoney(retainageHeld)} retainage`
    : null;

  const cells = [
    { label: 'Contract value', value: contractValue, mono: true, money: true },
    // 'SOV allocated' tile removed 2026-06-01 — equals Contract value on a
    // fully-allocated SOV and is noise for day-to-day billing. The
    // sovAllocated prop was dropped from the signature too; callers may
    // still pass it (ignored) without harm.
    {
      label: 'Billed to date',
      value: billedToDate,
      mono: true,
      money: true,
      annotation: retainageInline,
      annotationColor: 'var(--warning)',
    },
    { label: 'Remaining', value: remaining, mono: true, money: true },
    { label: 'Days remaining', value: daysRemaining, mono: true },
    {
      label: 'Collected',
      value: collectedToDate,
      mono: true,
      money: true,
      onClick: onEditCollected,
    },
    { label: 'Outstanding', value: outstanding, mono: true, money: true },
  ];

  return (
    <div style={{
      background: 'var(--bg-surface)',
      // Rounded card on --bg-page. overflow: hidden clips the inner
      // main row's overflow-x: auto scrollbar to the rounded shape
      // (and the expanded drawer's bottom-border to the corners).
      // KpiStrip's outer wrapper isn't sticky so this doesn't break
      // sticky positioning anywhere.
      border: '1px solid var(--border-subtle)',
      borderRadius: 12,
      overflow: 'hidden',
    }}>
      {/* Main row. min-height bumped 56→64 to comfortably fit
          11px label + 4px gap + 15px value + 12px vertical padding. */}
      <div
        style={{
          minHeight: 64,
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          padding: '12px 32px',
          // No bottom border here — the outer card's border + rounded
          // corners (added on the outermost div) handle the boundary.
          // When expanded, the dashed border on top of the drawer is
          // the only divider needed.
          borderBottom: 'none',
          fontFamily: 'var(--font-sans)',
          // 7 KPI cells + dividers + 180px billed-progress + 28px
          // chevron has a min-width around 1300-1500px. Cell labels +
          // money values use whiteSpace: nowrap so they refuse to
          // shrink. Without overflow handling here, the strip would
          // push the entire chrome wider than the viewport on common
          // laptop sizes, clipping the right edge of every chrome
          // surface (project header, last tab, end of the strip).
          // overflow-x: auto keeps the chrome capped at viewport width
          // and lets users scroll inside the strip to reach the cells
          // that don't fit.
          overflowX: 'auto', overflowY: 'visible',
        }}
      >
        {cells.map((cell, i) => (
          <React.Fragment key={cell.label}>
            <Cell {...cell} />
            {i < cells.length - 1 && <Divider />}
          </React.Fragment>
        ))}

        {/* Inline Billed Progress — pushed to the far right via auto
            margin so on wide screens the 8 cells stay grouped on the
            left and the progress + chevron sit flush against the right
            edge, per handoff §3 ("Inline Billed Progress at the right"). */}
        {typeof billedPercent === 'number' && (
          <>
            <div style={{ marginLeft: 'auto' }} />
            <Divider />
            <div style={{ width: 180, flexShrink: 0 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11.5,
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'var(--text-3)',
                  marginBottom: 4,
                }}
              >
                <span>Billed</span>
                <span className="mono" style={{ color: 'var(--text-2)' }}>
                  {formatPercent(billedPercent)}
                </span>
              </div>
              <Progress value={billedPercent} height={4} />
            </div>
          </>
        )}

      </div>
      {/* Removed: expand chevron + 4-tile drawer (Burn rate / Retainage
          held / This month billed / Forecast at completion). All four
          values were $0 on every project we tested and the chrome was
          carrying state + a noisy affordance for them. Retainage Held
          still surfaces inline next to the Billed to date cell via
          `retainageInline`. If the drawer comes back, restore the
          chevron button + the `{expanded && (...)}` block here. */}
    </div>
  );
}

/* -------------------- cell -------------------- */

function Cell({ label, value, mono, money, onClick, annotation, annotationColor }) {
  const empty = !isPopulated(value);
  // Label stays at text-2 (more contrast than spec's text-3 — needed
  // for dark-mode legibility at small font sizes). Value gets the
  // text-4 muting when empty so the "populated values pop" intent
  // still lands.
  const labelColor = 'var(--text-2)';
  const valueColor = empty ? 'var(--text-4)' : 'var(--text-1)';
  // Full money format (e.g. $73,857 instead of $74k). Plenty of
  // horizontal room in the KPI strip at typical widths, and full
  // values match what shows in the AFP detail header below — keeps
  // the user from having to mentally map "$74k" to "$73,857".
  const displayValue = money ? formatMoney(value || 0) : String(value ?? '—');

  const content = (
    <>
      <div
        style={{
          display: 'block',
          fontSize: 12.5,
          lineHeight: '14px',
          fontWeight: 500,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: labelColor,
          whiteSpace: 'nowrap',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {/* Value row: main number + optional inline annotation on the
          same line so the cell stays one-line-tall (no awkward stacked
          look). Annotation sits to the right of the value with a small
          gap, in --warning orange by default. Only renders when
          annotation is truthy AND the parent value is populated. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          whiteSpace: 'nowrap',
        }}
      >
        <span
          className={mono ? 'mono' : undefined}
          style={{
            fontSize: 16.5,
            lineHeight: '20px',
            fontWeight: 500,
            color: valueColor,
            letterSpacing: '-0.02em',
          }}
        >
          {displayValue}
        </span>
        {annotation && !empty && (
          <span
            className={mono ? 'mono' : undefined}
            style={{
              fontSize: 12.5,
              lineHeight: '14px',
              fontWeight: 500,
              color: annotationColor || 'var(--text-3)',
              letterSpacing: '-0.005em',
            }}
          >
            {annotation}
          </span>
        )}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          padding: '4px 6px',
          border: 'none',
          background: 'transparent',
          textAlign: 'left',
          cursor: 'pointer',
          borderRadius: 6,
          minWidth: 0,
          margin: '-4px -6px',
          transition: 'background .12s',
        }}
        title="Click to update"
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        {content}
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      {content}
    </div>
  );
}

function Divider() {
  return (
    <span
      style={{
        width: 1,
        height: 28,
        background: 'var(--border-subtle)',
        flexShrink: 0,
      }}
    />
  );
}

/** Treats 0, null, undefined, empty string as empty/muted. */
function isPopulated(v) {
  if (v === null || v === undefined || v === '') return false;
  const n = Number(v);
  if (Number.isFinite(n) && n === 0) return false;
  return true;
}
